import { createMarketApiFetch } from "@/lib/marketApiFetch";

const marketApiFetch = createMarketApiFetch();

export function createOperationKey(prefix: string): string {
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}:${rand}`;
}

async function postJson<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await marketApiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & {
    detail?: string;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `Request failed (${res.status})`,
    );
  }
  return data;
}

export async function postFinancialWorkAction(args: {
  workspaceId: string;
  actionType: string;
  operationKey: string;
  objectKind: string;
  objectId: string;
  requiredRevision: number;
  actorId?: string | null;
}): Promise<{
  status: string;
  operation_status?: string;
  operation_key?: string;
  mutated?: boolean;
  conflict?: boolean;
  message?: string;
}> {
  const res = await marketApiFetch("/api/v1/financial-work/actions", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      workspace_id: args.workspaceId,
      action_type: args.actionType,
      operation_key: args.operationKey,
      object_kind: args.objectKind,
      object_id: args.objectId,
      required_revision: args.requiredRevision,
      ...(args.actorId?.trim() ? { actor_id: args.actorId.trim() } : {}),
    }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    status?: string;
    operation_status?: string;
    operation_key?: string;
    mutated?: boolean;
    message?: string;
    detail?: string;
  };
  if (res.status === 409) {
    return {
      status: "conflict",
      conflict: true,
      operation_key: args.operationKey,
      message: data.message || data.detail || "การ์ดเก่า",
    };
  }
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.message === "string"
          ? data.message
          : `Financial work action failed (${res.status})`,
    );
  }
  return {
    status: data.status || "committed",
    operation_status: data.operation_status,
    operation_key: data.operation_key,
    mutated: data.mutated,
  };
}

export type MoneyOperationResult = {
  status?: string;
  operation_status?: string;
  operation_key?: string;
  after?: Record<string, unknown>;
  entity_id?: string;
};

export async function postMoveAllocation(args: {
  ownerUserId: string;
  workspaceId: string;
  allocationId: string;
  destinationReceivableId: string;
  amount?: string;
  expectedSourceRevision?: number;
  expectedDestinationRevision?: number;
  operationKey?: string;
}): Promise<MoneyOperationResult> {
  return postJson("/api/v1/payments/corrections/move", {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId,
    allocation_id: args.allocationId,
    destination_receivable_id: args.destinationReceivableId,
    amount: args.amount ?? "",
    expected_source_revision: args.expectedSourceRevision ?? 0,
    expected_destination_revision: args.expectedDestinationRevision ?? 0,
    operation_key: args.operationKey || createOperationKey("move"),
    channel: "web",
  });
}

export async function postResplitAllocation(args: {
  ownerUserId: string;
  workspaceId: string;
  paymentId: string;
  allocations: Array<{
    receivable_id: string;
    amount: string;
    expected_revision?: number;
  }>;
  operationKey?: string;
}): Promise<MoneyOperationResult> {
  return postJson("/api/v1/payments/corrections/resplit", {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId,
    payment_id: args.paymentId,
    allocations: args.allocations,
    operation_key: args.operationKey || createOperationKey("resplit"),
    channel: "web",
  });
}

export async function postCreateReceivableAndAllocate(args: {
  ownerUserId: string;
  workspaceId: string;
  paymentId: string;
  customerName: string;
  amount: string;
  allocateAmount?: string;
  sourceRef?: string;
  expectedPaymentRevision?: number;
  operationKey?: string;
}): Promise<MoneyOperationResult> {
  return postJson("/api/v1/payments/corrections/create-receivable-and-allocate", {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId,
    payment_id: args.paymentId,
    customer_name: args.customerName,
    amount: args.amount,
    allocate_amount: args.allocateAmount ?? "",
    source_ref: args.sourceRef ?? "",
    expected_payment_revision: args.expectedPaymentRevision ?? 0,
    operation_key: args.operationKey || createOperationKey("create_alloc"),
    channel: "web",
  });
}

export async function postUndoCorrection(args: {
  ownerUserId: string;
  workspaceId: string;
  targetOperationId: string;
  operationKey?: string;
}): Promise<MoneyOperationResult> {
  return postJson("/api/v1/payments/corrections/undo", {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId,
    target_operation_id: args.targetOperationId,
    operation_key: args.operationKey || createOperationKey("undo"),
    channel: "web",
  });
}

export type UndoImpact = {
  ok: boolean;
  blocked?: boolean;
  reason?: string;
  target_operation_id?: string;
  operation_type?: string;
  title?: string;
  description?: string;
  details?: string[];
  channel_label?: string;
  occurred_at?: string;
  actor_name?: string;
  summary_lines?: string[];
  payment_ids?: string[];
  receivable_ids?: string[];
};

const UNDO_BLOCK_REASON_TH: Record<string, string> = {
  ALREADY_UNDONE: "รายการนี้ถูกย้อนกลับไปแล้ว",
  LATER_OPERATION_EXISTS: "มีการแก้ไขใหม่กว่าบนยอดที่เกี่ยวข้องแล้ว — ไม่สามารถย้อนรายการนี้ได้",
  not_undoable: "รายการนี้ไม่สามารถย้อนกลับได้",
  target_operation_not_found: "ไม่พบรายการที่ย้อนกลับ",
  target_operation_id_required: "ไม่พบรายการที่ย้อนกลับ",
};

/** Prefer API summary_lines; fall back to Thai for known block reason codes. */
export function formatUndoBlockMessage(impact: {
  reason?: string;
  summary_lines?: string[];
} | null | undefined): string {
  const fromApi = impact?.summary_lines?.find((line) => String(line || "").trim());
  if (fromApi) return String(fromApi).trim();
  const reason = String(impact?.reason || "").trim();
  if (reason && UNDO_BLOCK_REASON_TH[reason]) return UNDO_BLOCK_REASON_TH[reason];
  if (reason && !/^[A-Z0-9_]+$/.test(reason)) return reason;
  return "ไม่สามารถย้อนกลับรายการนี้ได้";
}

export async function fetchUndoImpact(args: {
  ownerUserId: string;
  targetOperationId: string;
}): Promise<UndoImpact> {
  const qs = new URLSearchParams({
    owner_user_id: args.ownerUserId,
    target_operation_id: args.targetOperationId,
  });
  const res = await marketApiFetch(
    `/api/v1/payments/corrections/undo/impact?${qs}`,
    { headers: { Accept: "application/json" } },
  );
  const data = (await res.json().catch(() => ({}))) as UndoImpact & {
    detail?: string;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `โหลดผลกระทบการย้อนกลับไม่สำเร็จ (${res.status})`,
    );
  }
  return data;
}

export async function postAllocateMoney(args: {
  ownerUserId: string;
  workspaceId: string;
  paymentId?: string;
  allocations?: Array<{
    receivable_id: string;
    amount: string;
    expected_revision?: number;
  }>;
  pairs?: Array<{
    payment_id: string;
    receivable_id: string;
    amount: string;
    expected_revision?: number;
  }>;
  operationKey?: string;
}): Promise<MoneyOperationResult> {
  return postJson("/api/v1/payments/corrections/allocate", {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId,
    payment_id: args.paymentId ?? "",
    allocations: args.allocations ?? [],
    pairs: args.pairs ?? [],
    operation_key: args.operationKey || createOperationKey("allocate"),
    channel: "web",
  });
}

export function pickOperationId(result: MoneyOperationResult): string | null {
  const after = result.after;
  if (!after || typeof after !== "object") return null;
  const id = (after as { operation_id?: unknown }).operation_id;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

export async function fetchOperationStatus(operationKey: string): Promise<{
  status: string;
  operation_key?: string;
  operation_id?: string;
  after?: Record<string, unknown>;
  message?: string;
}> {
  const res = await marketApiFetch(
    `/api/v1/operations/${encodeURIComponent(operationKey)}`,
    { headers: { Accept: "application/json" } },
  );
  const data = (await res.json().catch(() => ({}))) as {
    status?: string;
    detail?: string;
    message?: string;
    error?: string;
    operation_key?: string;
    operation_id?: string;
    after?: Record<string, unknown>;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.message === "string"
          ? data.message
          : `Operation lookup failed (${res.status})`,
    );
  }
  const message =
    (typeof data.message === "string" && data.message.trim()) ||
    (typeof data.detail === "string" && data.detail.trim()) ||
    (typeof data.error === "string" && data.error.trim()) ||
    undefined;
  return {
    status: data.status || "not_found",
    operation_key: data.operation_key,
    operation_id: data.operation_id,
    after: data.after,
    message,
  };
}

export async function postDismissPending(args: {
  ownerUserId: string;
  workspaceId?: string;
  orderId: string;
}): Promise<{
  ok: boolean;
  order_id?: string;
  order_type?: string;
  summary_lines?: string[];
  reason?: string;
}> {
  return postJson("/api/v1/payments/corrections/dismiss-pending", {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId ?? "",
    order_id: args.orderId,
    channel: "web",
  });
}

export async function postEditPendingAddress(args: {
  ownerUserId: string;
  workspaceId?: string;
  orderId: string;
  addrName: string;
  addrPhone: string;
  addressText: string;
}): Promise<{
  ok: boolean;
  order_id?: string;
  order_type?: string;
  summary_lines?: string[];
  after?: {
    addr_name?: string;
    addr_phone?: string;
    address_text?: string;
  };
  reason?: string;
}> {
  return postJson("/api/v1/payments/corrections/edit-pending-address", {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId ?? "",
    order_id: args.orderId,
    addr_name: args.addrName,
    addr_phone: args.addrPhone,
    address_text: args.addressText,
    channel: "web",
  });
}

export async function postEditPendingSlipSender(args: {
  ownerUserId: string;
  workspaceId?: string;
  orderId: string;
  slipSender: string;
}): Promise<{
  ok: boolean;
  order_id?: string;
  order_type?: string;
  summary_lines?: string[];
  after?: {
    slip_sender?: string;
    sender_name?: string;
  };
  reason?: string;
}> {
  return postJson("/api/v1/payments/corrections/edit-pending-slip-sender", {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId ?? "",
    order_id: args.orderId,
    slip_sender: args.slipSender,
    channel: "web",
  });
}
